import { useCallback, useEffect, useRef } from 'react';

import { RiSearchLine } from '@remixicon/react';

import { SearchField } from '@backstage/ui';

import { useDebounced } from '../../hooks/useDebounced';
import { useFilterShortcut } from '../../hooks/useFilterShortcut';
import { useFilters } from './provider';

interface FilterSearchProps {
  searchCollapsed?: boolean;
  debounceTime?: number;
}

export const FilterSearch = ({
  searchCollapsed = false,
  debounceTime = 300,
}: FilterSearchProps) => {
  const { searchQuery, setQuery } = useFilters();
  const containerRef = useRef<HTMLDivElement>(null);
  const [filterShortcutEnabled] = useFilterShortcut();

  const [displayValue, handleChangeDebounced, handleChangeImmediately] =
    useDebounced<string>(searchQuery, setQuery, debounceTime);

  const handleKeyDown = useCallback(
    (event: {
      key: string;
      metaKey: boolean;
      shiftKey: boolean;
      preventDefault: () => void;
    }) => {
      if (event.key !== 'f' || !event.metaKey || event.shiftKey) return;
      if (!filterShortcutEnabled) return;

      const input = containerRef.current?.querySelector<HTMLElement>(
        'input, [role="searchbox"]',
      );
      if (!input) return;

      // If the input is already focused, let the browser search through.
      if (document.activeElement === input) return;

      event.preventDefault();
      input.focus();
    },
    [filterShortcutEnabled],
  );

  useEffect(() => {
    const listener = (event: KeyboardEvent) => handleKeyDown(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handleKeyDown]);

  return (
    <SearchField
      ref={containerRef}
      startCollapsed={searchCollapsed}
      name="filter-bar"
      placeholder="Filter..."
      value={displayValue}
      onChange={handleChangeDebounced}
      onClear={() => handleChangeImmediately('')}
      onKeyDown={handleKeyDown}
      icon={<RiSearchLine />}
      aria-label="search"
    />
  );
};
